import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import dcmjs from 'dcmjs'
import { checkCaseId, initiateUpload, uploadFile, batchInitUpload, batchCompleteUpload, uploadFileDirect, batchCompleteUrl, discardUploadSession } from '../services/api'
import { CONFIRM, confirmUploadWithRetry, sendConfirmBeacon } from '../services/confirmUpload'
import OsteoLogo from '../components/Logo'
import ScanRequirementsCard from '../components/ScanRequirementsCard'

// ── Constants ────────────────────────────────────────────────────
const STATUS = {
  QUEUED:         'queued',
  VALIDATING:     'validating',
  PREPARING:      'preparing',
  UPLOADING:      'uploading',
  UPLOADED:       'uploaded',
  FAILED:         'failed',
}

const UPLOAD_CONCURRENCY = 8

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

// ── DICOM Validation ──────────────────────────────────────────────
async function validateDicomFile(file) {
  try {
    let rawBuffer = await file.arrayBuffer()

    let wasGzipped = false
    const magic = new Uint8Array(rawBuffer, 0, 2)
    if (magic[0] === 0x1f && magic[1] === 0x8b) {
      rawBuffer = pako.ungzip(new Uint8Array(rawBuffer)).buffer
      wasGzipped = true
    }

    const dicomData = dcmjs.data.DicomMessage.readFile(rawBuffer)
    const ds = dcmjs.data.DicomMetaDictionary.naturalizeDataset(dicomData.dict)

    const checks = []

    const modality = ds.Modality
    const VALID_MODALITIES = ['CT', 'MR', 'PT']
    const modalityOk = VALID_MODALITIES.includes(modality)
    checks.push({
      name:     'Modality',
      value:    modality ?? 'Missing',
      expected: 'CT, MR or PT',
      status:   modalityOk ? 'PASSED' : 'FAILED',
      message:  modalityOk
        ? `${modality} — valid modality`
        : `Expected CT, MR or PT, found "${modality ?? 'missing'}"`,
    })

    const thickness = ds.SliceThickness != null ? parseFloat(ds.SliceThickness) : NaN
    if (!isNaN(thickness)) {
      const st = thickness <= 0.625 ? 'PASSED' : thickness <= 1.0 ? 'WARNING' : 'FAILED'
      checks.push({
        name:     'Slice Thickness',
        value:    `${thickness} mm`,
        expected: '≤ 0.625 mm (ideal), ≤ 1 mm (acceptable)',
        status:   st,
        message:  st === 'PASSED' ? `${thickness} mm — excellent`
          : st === 'WARNING'      ? `${thickness} mm — acceptable but not ideal`
          : `${thickness} mm — too thick for quality 3D printing`,
      })
    } else {
      checks.push({
        name: 'Slice Thickness', value: 'Missing', expected: '≤ 1 mm', status: 'FAILED',
        message: 'Slice Thickness tag (0018,0050) not found',
      })
    }

    const spacingRaw = ds.PixelSpacing
    const spacing = Array.isArray(spacingRaw)
      ? Math.max(...spacingRaw.map(Number))
      : spacingRaw != null ? parseFloat(spacingRaw) : NaN
    if (!isNaN(spacing)) {
      const ss = spacing <= 0.5 ? 'PASSED' : spacing <= 1.0 ? 'WARNING' : 'FAILED'
      checks.push({
        name:     'Pixel Spacing',
        value:    `${spacing.toFixed(4)} mm`,
        expected: '≤ 0.5 mm (ideal), ≤ 1 mm (acceptable)',
        status:   ss,
        message:  ss === 'PASSED' ? `${spacing.toFixed(4)} mm — excellent in-plane resolution`
          : ss === 'WARNING'      ? `${spacing.toFixed(4)} mm — acceptable but not ideal`
          : `${spacing.toFixed(4)} mm — insufficient in-plane resolution`,
      })
    }

    const rows = ds.Rows != null ? parseInt(ds.Rows) : NaN
    if (!isNaN(rows)) {
      checks.push({
        name: 'Image Rows', value: `${rows} px`, expected: '≥ 512 px',
        status: rows >= 512 ? 'PASSED' : 'FAILED',
        message: rows >= 512 ? `${rows} px` : `${rows} px — too small`,
      })
    }

    const cols = ds.Columns != null ? parseInt(ds.Columns) : NaN
    if (!isNaN(cols)) {
      checks.push({
        name: 'Image Columns', value: `${cols} px`, expected: '≥ 512 px',
        status: cols >= 512 ? 'PASSED' : 'FAILED',
        message: cols >= 512 ? `${cols} px` : `${cols} px — too small`,
      })
    }

    const bits = ds.BitsAllocated != null ? parseInt(ds.BitsAllocated) : NaN
    if (!isNaN(bits)) {
      checks.push({
        name: 'Bits Allocated', value: `${bits} bit`, expected: '16 bit',
        status: bits === 16 ? 'PASSED' : 'WARNING',
        message: bits === 16 ? '16-bit depth confirmed' : `${bits}-bit depth (16-bit preferred)`,
      })
    }

    const status = checks.some((c) => c.status === 'FAILED') ? 'FAILED'
      : checks.some((c) => c.status === 'WARNING') ? 'WARNING'
      : 'PASSED'

    return {
      status, checks, wasGzipped,
      // PatientName is deliberately NOT captured. It was stored in validationReportJson and
      // served from a public endpoint, and nothing ever rendered it — ValidationReport.jsx uses
      // seriesDescription and studyDate only. Not collecting it is the fix; stripping it on the
      // way out is only the backstop.
      meta: {
        studyDate:         ds.StudyDate ?? null,
        seriesDescription: ds.SeriesDescription ?? null,
        studyInstanceUID:  ds.StudyInstanceUID ?? null,
      },
    }
  } catch (err) {
    return {
      status: 'FAILED', wasGzipped: false,
      checks: [{ name: 'DICOM Parse', value: 'Error', expected: 'Valid DICOM file',
        status: 'FAILED', message: `Cannot parse file as DICOM: ${err.message}` }],
      meta: {},
    }
  }
}

// ── ZIP central-directory peek (no decompression) ─────────────────
// Reads only the central-directory at the end of the ZIP via File.slice —
// no decompression, no full file load.
// Returns { total, dicom } or null if file is not a valid ZIP.
async function peekZipFile(file) {
  try {
    const SEARCH_SIZE = Math.min(65558, file.size)
    const tail     = await file.slice(file.size - SEARCH_SIZE, file.size).arrayBuffer()
    const tailView = new DataView(tail)

    let eocdPos = -1
    for (let i = tail.byteLength - 22; i >= 0; i--) {
      if (tailView.getUint32(i, true) === 0x06054b50) { eocdPos = i; break }
    }
    if (eocdPos === -1) return null

    const cdSize   = tailView.getUint32(eocdPos + 12, true)
    const cdOffset = tailView.getUint32(eocdPos + 16, true)
    if (cdOffset === 0xFFFFFFFF) return null // ZIP64

    const cd     = await file.slice(cdOffset, cdOffset + cdSize).arrayBuffer()
    const cdView = new DataView(cd)
    const cdArr  = new Uint8Array(cd)
    const dec    = new TextDecoder('utf-8')

    let total = 0, dicom = 0, offset = 0
    while (offset + 46 <= cd.byteLength) {
      if (cdView.getUint32(offset, true) !== 0x02014b50) break
      const fnLen = cdView.getUint16(offset + 28, true)
      const exLen = cdView.getUint16(offset + 30, true)
      const cmLen = cdView.getUint16(offset + 32, true)
      const name  = dec.decode(cdArr.slice(offset + 46, offset + 46 + fnLen))
      if (!name.endsWith('/')) {
        total++
        const ext = name.split('.').pop().toLowerCase()
        if (ext === 'dcm' || ext === 'ima' || ext === 'dicom') dicom++
      }
      offset += 46 + fnLen + exLen + cmLen
    }
    return { total, dicom }
  } catch {
    return null
  }
}

// ── Main page ─────────────────────────────────────────────────────
export default function UploadPage() {
  const { caseId } = useParams()
  const navigate   = useNavigate()

  const [caseValid,       setCaseValid]       = useState(null)
  const [consentGiven,    setConsentGiven]    = useState(false)
  const [files,           setFiles]           = useState([])
  const [uploading,       setUploading]       = useState(false)
  const [prepProgress,    setPrepProgress]    = useState(null) // { current, total } during batch-init chunking
  const [paused,          setPaused]          = useState(false)
  const [allDone,         setAllDone]         = useState(false)
  const [confirmState,    setConfirmState]    = useState(CONFIRM.IDLE)
  const [confirmAttempt,  setConfirmAttempt]  = useState(null)
  const [confirmError,    setConfirmError]    = useState(null)
  const [retrying,        setRetrying]        = useState(false)
  const [unconfirmedDismissed, setUnconfirmedDismissed] = useState(false)
  const [pickMode,        setPickMode]        = useState('compressed') // 'compressed' | 'folder'
  const [studyValidation, setStudyValidation] = useState(null)

  const filesRef           = useRef([])
  const folderInputRef     = useRef(null)
  const compressedInputRef = useRef(null)
  const studyValidationRef = useRef(null)
  const sessionFolderRef   = useRef(null)
  const pausedRef          = useRef(false)
  const abortedRef         = useRef(false)
  const abortControllerRef = useRef(null)
  const speedRef           = useRef({ lastBytes: 0, lastTime: 0, speed: 0 })
  // Holds the batch-complete payload until the backend confirms it, so a
  // closing tab can still flush it via sendBeacon.
  const pendingConfirmRef  = useRef(null)

  // ── Verify Case ID on mount ───────────────────────────────────
  useEffect(() => {
    checkCaseId(caseId)
      .then(() => setCaseValid(true))
      .catch(() => { setCaseValid(false); setTimeout(() => navigate('/', { replace: true }), 3000) })
  }, [caseId, navigate])

  // ── Warn before leaving mid-upload ───────────────────────────
  // Also warns while a finished upload is still unconfirmed — leaving then
  // means the files sit in Drive with no record of them.
  useEffect(() => {
    const unconfirmed = confirmState === CONFIRM.CONFIRMING || confirmState === CONFIRM.UNCONFIRMED
    if (!uploading && !unconfirmed) return
    const handler = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [uploading, confirmState])

  // ── Flush an unconfirmed session if the page goes away ───────
  // beforeunload can be skipped entirely (mobile, tab discard); pagehide is
  // the one that reliably fires, and sendBeacon survives the teardown.
  useEffect(() => {
    const handler = () => {
      if (pendingConfirmRef.current) {
        sendConfirmBeacon({ url: batchCompleteUrl(), payload: pendingConfirmRef.current })
      }
    }
    window.addEventListener('pagehide', handler)
    return () => window.removeEventListener('pagehide', handler)
  }, [])

  // ── File state helpers ────────────────────────────────────────
  function updateFile(id, patch) {
    setFiles((prev) => {
      const next = prev.map((f) => (f.id === id ? { ...f, ...patch } : f))
      filesRef.current = next
      return next
    })
  }

  function removeFile(id) {
    setFiles((prev) => {
      const next = prev.filter((f) => f.id !== id)
      filesRef.current = next
      return next
    })
  }

  function addFiles(rawFiles, isCompressed = false) {
    const newItems = rawFiles.map((file) => {
      // Extract subfolder path from webkitRelativePath (strip top folder + filename)
      // e.g. "PatientData/CT/Series1/IM001.dcm" → "CT/Series1"
      let subfolderPath = null
      if (file.webkitRelativePath) {
        const parts = file.webkitRelativePath.split('/')
        if (parts.length > 2) {
          subfolderPath = parts.slice(1, -1).join('/')
        }
      }
      return {
      id: crypto.randomUUID(), file,
      status: STATUS.QUEUED, progress: 0, error: null,
      uploadId: null, driveFileId: null,
      validationStatus: isCompressed ? 'PENDING' : null,
      validationResult: null,
      zipPeek: null,
      isCompressed,
      subfolderPath,
    }
    })
    setFiles((prev) => { const next = [...prev, ...newItems]; filesRef.current = next; return next })

    // Peek inside ZIP files to show entry count before upload
    if (isCompressed) {
      newItems.forEach(async (item) => {
        if (item.file.name.toLowerCase().endsWith('.zip')) {
          const peek = await peekZipFile(item.file)
          if (peek) updateFile(item.id, { zipPeek: peek })
        }
      })
    }
  }

  // Dropzone registered for potential future drag-drop; suppress unused vars
  const onDrop = useCallback(() => {}, [])
  // eslint-disable-next-line no-unused-vars
  const { getRootProps, getInputProps } = useDropzone({ onDrop, disabled: true, noClick: true })

  const COMPRESSED_EXTS = new Set(['zip', 'gz', 'tar', 'bz2', '7z', 'rar'])
  function isArchive(file) {
    const ext = file.name.split('.').pop().toLowerCase()
    return COMPRESSED_EXTS.has(ext)
  }

  function onFolderSelect(e) {
    const selected = Array.from(e.target.files)
    if (selected.length === 0) { e.target.value = ''; return }
    // Auto-detect: archives in the folder are treated as compressed data
    const dicomFiles      = selected.filter((f) => !isArchive(f))
    const archiveFiles    = selected.filter((f) =>  isArchive(f))
    if (dicomFiles.length  > 0) addFiles(dicomFiles,   false)
    if (archiveFiles.length > 0) addFiles(archiveFiles, true)
    e.target.value = ''
  }

  function onCompressedSelect(e) {
    const selected = Array.from(e.target.files)
    if (selected.length > 0) addFiles(selected, true)
    e.target.value = ''
  }

  // ── Upload a single file directly to Drive (no backend calls) ──
  async function uploadOneDirect(item, signal) {
    const { id, file } = item
    updateFile(id, { status: STATUS.UPLOADING, progress: 0 })
    try {
      const driveFileId = await uploadFileDirect(
        item.uploadUrl, file,
        (loaded, total) => updateFile(id, { progress: Math.round((loaded / total) * 100) }),
        signal,
      )
      updateFile(id, { status: STATUS.UPLOADED, progress: 100, driveFileId })
      return { uploadId: item.uploadId, driveFileId, fileSize: file.size }
    } catch (err) {
      if (abortedRef.current || pausedRef.current || err.message === 'Upload aborted') {
        updateFile(id, { status: STATUS.QUEUED, progress: 0, error: null })
      } else {
        updateFile(id, { status: STATUS.FAILED, error: err.message || 'Upload failed.' })
      }
      return null
    }
  }

  // ── Fallback: single-file upload (for compressed archives / retries) ──
  async function uploadOneLegacy(item, sessionFolder, skipValidation) {
    const { id, file, subfolderPath } = item
    let validationResult

    if (skipValidation) {
      validationResult = { status: 'PENDING', checks: [], wasGzipped: false, meta: {} }
      updateFile(id, { validationStatus: 'PENDING', validationResult })
    } else if (studyValidationRef.current) {
      validationResult = studyValidationRef.current
      updateFile(id, { validationStatus: validationResult.status, validationResult })
    } else {
      updateFile(id, { status: STATUS.VALIDATING, progress: 0 })
      validationResult = await validateDicomFile(file)
      if (abortedRef.current) { updateFile(id, { status: STATUS.QUEUED, progress: 0, error: null }); return }
      studyValidationRef.current = validationResult
      setStudyValidation(validationResult)
      updateFile(id, { validationStatus: validationResult.status, validationResult })
    }

    if (abortedRef.current) { updateFile(id, { status: STATUS.QUEUED, progress: 0, error: null }); return }

    updateFile(id, { status: STATUS.UPLOADING, progress: 0 })
    try {
      const { data: { uploadId } } = await initiateUpload(
        caseId, file.name, file.size,
        validationResult.status, skipValidation ? null : validationResult,
        sessionFolder, subfolderPath,
      )
      updateFile(id, { uploadId })

      const controller = new AbortController()
      abortControllerRef.current = controller
      await uploadFile(uploadId, file, (loaded, total) => {
        updateFile(id, { progress: Math.round((loaded / total) * 100) })
      }, controller.signal)

      updateFile(id, { status: STATUS.UPLOADED, progress: 100 })
    } catch (err) {
      if (abortedRef.current || pausedRef.current || err.message === 'Upload aborted') {
        updateFile(id, { status: STATUS.QUEUED, progress: 0, error: null })
      } else {
        updateFile(id, { status: STATUS.FAILED, error: err.response?.data?.error || err.message || 'Upload failed.' })
      }
    }
  }

  // ── Start / retry / resume upload queue ───────────────────────
  async function startUpload() {
    if (!pausedRef.current && !sessionFolderRef.current) {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      sessionFolderRef.current = [
        now.getFullYear(), pad(now.getMonth() + 1), pad(now.getDate()),
        pad(now.getHours()), pad(now.getMinutes()), pad(now.getSeconds()),
      ].join('-')
      studyValidationRef.current = null
      setStudyValidation(null)
    }

    pausedRef.current  = false
    abortedRef.current = false
    setPaused(false)
    setUploading(true)
    setAllDone(false)
    setConfirmState(CONFIRM.IDLE)
    setConfirmError(null)
    setConfirmAttempt(null)
    setRetrying(false)
    setUnconfirmedDismissed(false)

    const skipValidation = pickMode === 'compressed'
    const pendingItems = filesRef.current.filter(
      (f) => f.status === STATUS.QUEUED || f.status === STATUS.FAILED,
    )

    // ── Unified batch flow for ALL upload types ─────────────────

    // Step 1: DICOM validation (skip for compressed archives)
    if (!skipValidation) {
      if (!studyValidationRef.current && pendingItems.length > 0) {
        const first = pendingItems[0]
        updateFile(first.id, { status: STATUS.VALIDATING, progress: 0 })
        const validationResult = await validateDicomFile(first.file)
        if (abortedRef.current) {
          updateFile(first.id, { status: STATUS.QUEUED, progress: 0, error: null })
          setUploading(false)
          abortedRef.current = false
          return
        }
        studyValidationRef.current = validationResult
        setStudyValidation(validationResult)
      }
    }

    // Apply validation result to all files
    const valStatus = skipValidation ? 'PENDING' : (studyValidationRef.current?.status || 'PENDING')
    for (const item of pendingItems) {
      updateFile(item.id, {
        validationStatus: valStatus,
        validationResult: skipValidation ? null : studyValidationRef.current,
      })
    }

    // Step 2+3: Prep and upload concurrently — start uploading as soon
    // as the first batch-init chunk returns, while prepping the rest.
    const INIT_CHUNK = 100
    const totalChunks = Math.ceil(pendingItems.length / INIT_CHUNK)
    const allFiles = pendingItems.map((item) => ({
      filename:      item.file.name,
      fileSize:      item.file.size,
      subfolderPath: item.subfolderPath,
    }))

    for (const item of pendingItems) {
      updateFile(item.id, { status: STATUS.PREPARING })
    }
    if (totalChunks > 1) setPrepProgress({ current: 0, total: totalChunks })

    // readyItems: items with uploadId + uploadUrl assigned, ready to upload
    let readyIdx = 0
    const completedResults = []
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    const pool = new Set()
    let prepDone = false
    let prepError = null

    // Launch prep in background — it populates pendingItems with uploadId/uploadUrl
    const prepPromise = (async () => {
      try {
        for (let c = 0; c < allFiles.length; c += INIT_CHUNK) {
          if (pausedRef.current || abortedRef.current) break
          const chunkIdx = Math.floor(c / INIT_CHUNK)
          if (totalChunks > 1) setPrepProgress({ current: chunkIdx + 1, total: totalChunks })
          const chunk = allFiles.slice(c, c + INIT_CHUNK)
          const { data } = await batchInitUpload(
            caseId,
            sessionFolderRef.current,
            chunk,
            valStatus,
            c === 0 ? (skipValidation ? null : studyValidationRef.current) : null,
          )
          // Map uploadId/uploadUrl onto pendingItems as they arrive
          for (let j = 0; j < data.uploads.length; j++) {
            const itemIdx = c + j
            const info = data.uploads[j]
            pendingItems[itemIdx].uploadId  = info.uploadId
            pendingItems[itemIdx].uploadUrl = info.uploadUrl
            updateFile(pendingItems[itemIdx].id, { uploadId: info.uploadId })
          }
        }
        setPrepProgress(null)
        prepDone = true
      } catch (err) {
        prepError = err
        prepDone = true
        setPrepProgress(null)
      }
    })()

    // Upload loop — consumes ready items while prep feeds more
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (pausedRef.current || abortedRef.current) {
        abortController.abort()
        for (let j = readyIdx; j < pendingItems.length; j++) {
          if (!pool.has(pendingItems[j].id)) {
            updateFile(pendingItems[j].id, { status: STATUS.QUEUED, progress: 0, error: null })
          }
        }
        break
      }

      // Feed ready items into the upload pool
      while (readyIdx < pendingItems.length && pool.size < UPLOAD_CONCURRENCY) {
        if (pausedRef.current || abortedRef.current) break
        const item = pendingItems[readyIdx]
        if (!item.uploadUrl) break // not prepped yet — wait
        readyIdx++
        const promise = uploadOneDirect(item, abortController.signal)
          .then((result) => {
            pool.delete(promise)
            if (result) completedResults.push(result)
          })
        pool.add(promise)
      }

      // All items consumed and pool empty — done
      if (readyIdx >= pendingItems.length && pool.size === 0 && prepDone) break
      // Prep failed and nothing in flight — stop waiting for items that will never arrive
      if (prepDone && prepError && pool.size === 0) break

      // Wait for either an upload to finish or a short delay (to check for new prepped items)
      if (pool.size > 0) {
        await Promise.race(pool)
      } else {
        // No uploads in flight, waiting for prep — short sleep
        await new Promise((r) => setTimeout(r, 200))
      }
    }

    await prepPromise // ensure prep is fully done

    // If prep failed and no uploads succeeded, mark remaining as failed
    if (prepError && completedResults.length === 0) {
      for (const item of pendingItems) {
        updateFile(item.id, {
          status: STATUS.FAILED,
          error: prepError.response?.data?.error || 'Failed to prepare upload session.',
        })
      }
      setUploading(false)
      return
    }

    // Prep failed *after* some files had already uploaded: anything that never
    // got an upload URL is still sitting in PREPARING. Leaving it there is a
    // dead end — canUpload only counts QUEUED and FAILED, so the Upload button
    // stays disabled and the doctor cannot retry without reloading the page.
    // A pause or cancel is not a failure — those paths reset files to QUEUED
    // deliberately, so leave them alone.
    if (prepError && !pausedRef.current && !abortedRef.current) {
      const stuckMessage = prepError.response?.data?.error || 'Failed to prepare upload session.'
      for (const item of pendingItems) {
        if (!item.uploadUrl) {
          updateFile(item.id, { status: STATUS.FAILED, error: stuckMessage })
        }
      }
    }

    // Step 4: Batch complete — tell the backend what landed in Drive.
    // This is awaited and retried, not fired and forgotten: until it succeeds
    // the files exist in Drive but not in the portal, and nobody is notified.
    let confirmed = true
    if (completedResults.length > 0 && !abortedRef.current) {
      const payload = {
        caseId,
        sessionFolder:    sessionFolderRef.current,
        validationStatus: valStatus,
        results:          completedResults,
      }
      pendingConfirmRef.current = payload
      confirmed = await runConfirm(payload)
    }

    setUploading(false)

    if (abortedRef.current) {
      abortedRef.current = false
      setPaused(false)
    } else if (pausedRef.current) {
      // stay paused — user will click Resume
    } else {
      setPaused(false)
      const final = filesRef.current
      // Success means every file actually reached Drive AND the backend
      // recorded the session. "Nothing marked failed" is not the same thing —
      // an aborted file is reset to QUEUED, which used to render a green tick
      // over an upload where nothing landed.
      const allUploaded = final.length > 0 && final.every((f) => f.status === STATUS.UPLOADED)
      if (allUploaded && confirmed) setAllDone(true)
    }
  }

  // Runs batch-complete with retry. Returns true once the backend has it.
  async function runConfirm(payload) {
    setConfirmState(CONFIRM.CONFIRMING)
    setConfirmAttempt(null)

    const { ok, error } = await confirmUploadWithRetry({
      send: () => batchCompleteUpload(
        payload.caseId,
        payload.sessionFolder,
        payload.validationStatus,
        payload.results,
      ),
      onAttempt: (n, total) => setConfirmAttempt(n > 1 ? { n, total } : null),
    })

    if (ok) {
      pendingConfirmRef.current = null
      setConfirmState(CONFIRM.CONFIRMED)
      setUnconfirmedDismissed(false)
      return true
    }

    setConfirmState(CONFIRM.UNCONFIRMED)
    setConfirmError(error?.response?.data?.error || error?.message || 'Could not reach the server.')
    return false
  }

  // Returns to the file list without pretending the upload was recorded: the
  // amber banner stays, the unload warning stays, and the pending payload is
  // still flushed by the beacon. Without this a doctor who paused mid-upload
  // is stuck on the retry screen with no way back to Resume.
  function handleDismissUnconfirmed() {
    setUnconfirmedDismissed(true)
  }

  async function handleRetryConfirm() {
    const payload = pendingConfirmRef.current
    if (!payload) return
    // `retrying` keeps this screen mounted while runConfirm flips the state to
    // CONFIRMING — otherwise the retry screen would unmount on click and the
    // doctor would be thrown back to the file list mid-retry.
    setRetrying(true)
    try {
      const ok = await runConfirm(payload)
      if (ok) {
        const final = filesRef.current
        if (final.length > 0 && final.every((f) => f.status === STATUS.UPLOADED)) setAllDone(true)
      }
    } finally {
      setRetrying(false)
    }
  }

  function handlePause() {
    pausedRef.current = true
    setPaused(true)
    // Abort the in-flight XHR so it stops immediately — the file resets to QUEUED
    // and Resume will re-upload it from scratch.
    abortControllerRef.current?.abort()
  }
  // Tells the backend a session ended deliberately, so the rows batch-init
  // created are removed instead of being reported two hours later as an upload
  // that "never completed". The backend only deletes rows that were never
  // recorded, so this can never touch a file that actually landed.
  // Fire-and-forget: a failed call costs nothing worse than one spurious alert.
  function discardSessionRecords() {
    const ids = filesRef.current.map((f) => f.uploadId).filter(Boolean)
    if (ids.length > 0) discardUploadSession(caseId, ids).catch(() => {})
    pendingConfirmRef.current = null
    // The session is gone, so any "not confirmed" warning about it is stale.
    // Leaving it up would show an amber banner whose Retry button has no
    // payload left to send — a warning the doctor cannot act on.
    setConfirmState(CONFIRM.IDLE)
    setConfirmError(null)
    setConfirmAttempt(null)
    setUnconfirmedDismissed(false)
  }

  function handleAbort() {
    abortedRef.current = true
    abortControllerRef.current?.abort()
    discardSessionRecords()
  }

  function handleCancelAll() {
    // Abort any in-flight XHR so the upload loop exits cleanly
    abortedRef.current = true
    abortControllerRef.current?.abort()

    discardSessionRecords()
    setPaused(false)
    pausedRef.current = false
    setUploading(false)
    setFiles([]); filesRef.current = []
    setStudyValidation(null); studyValidationRef.current = null
    sessionFolderRef.current = null
  }

  function clearAll() {
    setFiles([]); filesRef.current = []
    setStudyValidation(null); studyValidationRef.current = null
    sessionFolderRef.current = null
  }

  // ── Derived stats ─────────────────────────────────────────────
  const totalBytes    = files.reduce((s, f) => s + f.file.size, 0)
  const uploadedBytes = files.reduce((s, f) => {
    if (f.status === STATUS.UPLOADED)  return s + f.file.size
    if (f.status === STATUS.UPLOADING) return s + (f.file.size * f.progress) / 100
    return s
  }, 0)
  const rawPct        = totalBytes > 0 ? (uploadedBytes / totalBytes) * 100 : 0
  // Don't show 100% until every file is actually done
  const overallPct    = rawPct >= 100 && files.some((f) => f.status !== STATUS.UPLOADED && f.status !== STATUS.FAILED) ? 99 : Math.round(rawPct)
  const uploadedCount = files.filter((f) => f.status === STATUS.UPLOADED).length

  // Speed calculation — update every 2 seconds to avoid flicker
  const now = Date.now()
  if (uploading && now - speedRef.current.lastTime > 2000 && uploadedBytes > speedRef.current.lastBytes) {
    const elapsed = (now - speedRef.current.lastTime) / 1000
    speedRef.current.speed = (uploadedBytes - speedRef.current.lastBytes) / elapsed
    speedRef.current.lastBytes = uploadedBytes
    speedRef.current.lastTime = now
  }
  if (!uploading) { speedRef.current = { lastBytes: 0, lastTime: 0, speed: 0 } }
  const uploadSpeed = speedRef.current.speed
  const remainingBytes = totalBytes - uploadedBytes
  const etaSeconds = uploadSpeed > 0 ? Math.round(remainingBytes / uploadSpeed) : 0
  const failedCount   = files.filter((f) => f.status === STATUS.FAILED).length
  const queuedCount   = files.filter((f) => f.status === STATUS.QUEUED).length
  const canUpload     = !uploading && !paused && (queuedCount > 0 || failedCount > 0)

  // ── Loading / invalid states ──────────────────────────────────
  if (caseValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner className="w-8 h-8 text-brand-600 mx-auto" />
          <p className="mt-3 text-gray-500 text-sm">Verifying Case ID…</p>
        </div>
      </div>
    )
  }

  if (caseValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="card max-w-sm w-full text-center">
          <p className="text-red-600 font-medium">Case ID invalid or inactive.</p>
          <p className="text-gray-400 text-sm mt-1">Redirecting you back…</p>
        </div>
      </div>
    )
  }

  // ── Uploaded but not recorded ─────────────────────────────────
  // The files reached Drive but the backend never acknowledged the session.
  // Never show a green tick here: to the team this upload does not exist.
  if ((confirmState === CONFIRM.UNCONFIRMED && !unconfirmedDismissed) || retrying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="card max-w-md w-full text-center">
          <div className="flex justify-center mb-5"><OsteoLogo size="md" /></div>
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-4">
            <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Upload not confirmed</h2>
          <p className="text-gray-500 text-sm mb-3">
            Your files were sent, but we could not confirm them with the Osteo3d server.
            Please keep this page open and retry — otherwise your upload may not reach our team.
          </p>
          {confirmError && (
            <p className="text-xs text-gray-400 mb-3">Reason: {confirmError}</p>
          )}
          <p className="text-xs text-gray-400 mb-6">Case ID: <span className="font-mono font-medium">{caseId}</span></p>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleRetryConfirm}
              disabled={retrying}
              className="btn-primary text-sm disabled:opacity-60"
            >
              {retrying
                ? (confirmAttempt ? `Retrying… (${confirmAttempt.n} of ${confirmAttempt.total})` : 'Retrying…')
                : 'Retry confirmation'}
            </button>
            <button
              onClick={handleDismissUnconfirmed}
              disabled={retrying}
              className="btn-secondary text-sm disabled:opacity-60"
            >
              Back to files
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            If this keeps failing, contact Osteo3d with your Case ID — your files may already be with us.
          </p>
        </div>
      </div>
    )
  }

  // ── Success screen ────────────────────────────────────────────
  if (allDone) {
    const warnCount      = files.filter((f) => f.validationStatus === 'WARNING').length
    const failedValCount = files.filter((f) => f.validationStatus === 'FAILED').length
    const pendingCount   = files.filter((f) => f.validationStatus === 'PENDING').length
    // Count what actually landed, not what was picked. These used to be
    // files.length / totalBytes, which reported the doctor's selection back to
    // them as if it had been uploaded.
    const doneFiles      = files.filter((f) => f.status === STATUS.UPLOADED)
    const doneBytes      = doneFiles.reduce((s, f) => s + f.file.size, 0)

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="card max-w-md w-full text-center">
          <div className="flex justify-center mb-5"><OsteoLogo size="md" /></div>
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-4">
            <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Upload Complete</h2>
          <p className="text-gray-500 text-sm mb-1">
            {doneFiles.length} {doneFiles.length === 1 ? 'file' : 'files'} ({formatBytes(doneBytes)}) uploaded successfully.
          </p>
          {pendingCount > 0 && (
            <p className="text-blue-600 text-xs font-medium mb-1">
              Compressed {pendingCount === 1 ? 'archive' : 'archives'} uploaded — admin will verify DICOM contents
            </p>
          )}
          {failedValCount > 0 && (
            <p className="text-red-500 text-xs font-medium mb-1">
              {failedValCount} {failedValCount === 1 ? 'file' : 'files'} failed DICOM validation — admin has been notified
            </p>
          )}
          {warnCount > 0 && failedValCount === 0 && (
            <p className="text-yellow-600 text-xs font-medium mb-1">
              {warnCount} {warnCount === 1 ? 'file' : 'files'} passed with warnings
            </p>
          )}
          <p className="text-xs text-gray-400 mb-6">Case ID: <span className="font-mono font-medium">{caseId}</span></p>
          <div className="flex flex-col gap-2">
            <button onClick={() => navigate(`/report/${caseId}`)} className="btn-primary text-sm">
              View Validation Report
            </button>
            <button onClick={() => { clearAll(); setAllDone(false) }} className="btn-secondary text-sm">
              Upload more files
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Main upload UI ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <OsteoLogo size="sm" />
            <div className="border-l border-gray-200 pl-4">
              <p className="text-sm font-semibold text-gray-700 leading-none">Upload Medical Data</p>
              <p className="text-xs text-gray-400 font-mono mt-0.5">Case ID: {caseId}</p>
            </div>
          </div>
          <button onClick={() => navigate('/')} className="text-xs text-gray-400 hover:text-gray-600">← Back</button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-5">

        {/* ── Scan requirements ─────────────────────────────────── */}
        {/* First thing on the page: this link is forwarded on to the imaging
            centre, and is usually the only Osteo3d surface the radiographer sees. */}
        <ScanRequirementsCard />

        {/* ── Unconfirmed session banner ────────────────────────── */}
        {/* Shown after "Back to files" so dismissing the retry screen never
            hides the fact that an earlier batch was not recorded. */}
        {confirmState === CONFIRM.UNCONFIRMED && unconfirmedDismissed && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-amber-800">Earlier upload not confirmed</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Those files may not have reached our team. Please retry before closing this page.
              </p>
            </div>
            <button
              onClick={handleRetryConfirm}
              disabled={retrying}
              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 flex-shrink-0"
            >
              {retrying ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {/* ── Data-handling consent ─────────────────────────────── */}
        {!consentGiven ? (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Data Handling Notice</p>
                <p className="text-xs text-gray-500 mt-0.5">How your uploaded data is handled. Please read before uploading.</p>
              </div>
            </div>
            <ul className="text-xs text-gray-600 space-y-1.5 mb-4 pl-1">
              <li className="flex items-start gap-1.5">
                <span className="text-blue-400 mt-0.5">•</span>
                All data is encrypted in transit using TLS
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-blue-400 mt-0.5">•</span>
                Files are stored in Osteo3d&apos;s Secured Storage — not retained on this portal
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-blue-400 mt-0.5">•</span>
                Only authorised Osteo3d clinical staff can access uploaded files
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-blue-400 mt-0.5">•</span>
                De-identify at your PACS or scanner before uploading — files are stored as sent
              </li>
            </ul>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 cursor-pointer flex-shrink-0"
              />
              <span className="text-xs text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">
                I confirm that I am authorised to share this patient&apos;s DICOM data with Osteo3d for
                processing, and that appropriate consent has been obtained by my institution.
              </span>
            </label>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2 w-fit">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            Data handling acknowledged
          </div>
        )}

        {/* Mode toggle */}
        <div className={[
          'flex items-center gap-2 bg-white rounded-xl border border-gray-200 p-1 w-fit',
          !consentGiven ? 'opacity-50' : '',
        ].join(' ')}>
          {[
            { mode: 'compressed',
              label: 'Select Compressed Data',
              icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
            { mode: 'folder',
              label: 'Select Data',
              icon: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z' },
          ].map(({ mode, label, icon }) => (
            <button
              key={mode}
              onClick={() => { if (!uploading && consentGiven) { setPickMode(mode); clearAll() } }}
              disabled={uploading || !consentGiven}
              className={[
                'flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors',
                !consentGiven ? 'text-gray-400 cursor-not-allowed'
                  : pickMode === mode ? 'bg-brand-600 text-white' : 'text-gray-500 hover:text-gray-700',
              ].join(' ')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
              </svg>
              {label}
            </button>
          ))}
        </div>

        {/* Compressed mode info banner */}
        {pickMode === 'compressed' && files.length === 0 && (
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              Upload a pre-compressed archive (.zip, .gz, .tar, .bz2, .7z, .rar). DICOM contents cannot be validated
              in the browser — the admin will verify the archive after it lands on Drive.
              ZIP archives show a file count preview instantly.
            </span>
          </div>
        )}

        {/* Drop zone / picker */}
        {pickMode === 'folder' ? (
          <div
            onClick={() => !uploading && consentGiven && folderInputRef.current?.click()}
            className={[
              'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
              'border-gray-300 bg-white hover:border-brand-400 hover:bg-gray-50',
              (uploading || !consentGiven) ? 'opacity-50 pointer-events-none' : '',
            ].join(' ')}
          >
            <input ref={folderInputRef} type="file" className="hidden" webkitdirectory="" multiple onChange={onFolderSelect} />
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium">Click to select a folder</p>
            <p className="text-gray-400 text-sm mt-1">All files inside will be queued for upload</p>
            <p className="text-xs text-gray-300 mt-2">DICOM files validated · archives auto-detected &amp; uploaded as-is</p>
          </div>
        ) : (
          <div
            onClick={() => !uploading && consentGiven && compressedInputRef.current?.click()}
            className={[
              'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
              'border-gray-300 bg-white hover:border-brand-400 hover:bg-gray-50',
              (uploading || !consentGiven) ? 'opacity-50 pointer-events-none' : '',
            ].join(' ')}
          >
            <input
              ref={compressedInputRef}
              type="file"
              className="hidden"
              multiple
              accept=".zip,.gz,.tar,.tar.gz,.bz2,.7z,.rar"
              onChange={onCompressedSelect}
            />
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <p className="text-gray-700 font-medium">Click to select compressed archive(s)</p>
            <p className="text-gray-400 text-sm mt-1">Accepts .zip · .gz · .tar · .bz2 · .7z · .rar</p>
            <p className="text-xs text-gray-300 mt-2">Uploaded as-is · Admin validates DICOM contents after receipt</p>
          </div>
        )}

        {/* Info banner — folder mode, before first validation */}
        {pickMode === 'folder' && files.length > 0 && !studyValidation && !uploading && (
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            The first DICOM file will be validated to assess medical imaging dataset quality for the series.
            All files upload to Drive even if validation flags issues — the admin will review.
            Any compressed archives in the folder are uploaded as-is and marked for admin review.
          </div>
        )}

        {/* Study-level validation banner */}
        {studyValidation && pickMode === 'folder' && (
          <StudyValidationBanner result={studyValidation} fileCount={files.length} />
        )}

        {/* File list */}
        {files.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {files.length} {files.length === 1 ? 'file' : 'files'} · {formatBytes(totalBytes)}
              </span>
              {!uploading && !paused && (
                <button onClick={clearAll} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                  Clear all
                </button>
              )}
            </div>
            <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {files.map((item) => (
                <FileRow key={item.id} item={item} onRemove={() => removeFile(item.id)} uploading={uploading} />
              ))}
            </ul>
          </div>
        )}

        {/* Overall progress bar */}
        {(uploading || paused) && files.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600 font-medium">
                {confirmState === CONFIRM.CONFIRMING
                  ? (confirmAttempt
                      ? `Confirming with server… (retry ${confirmAttempt.n} of ${confirmAttempt.total})`
                      : 'Confirming with server…')
                  : paused
                    ? `Paused — ${uploadedCount} of ${files.length} done`
                    : prepProgress
                      ? `Preparing upload session… (batch ${prepProgress.current} of ${prepProgress.total})`
                      : `Processing ${uploadedCount + 1} of ${files.length}…`
                }
              </span>
              <span className="text-gray-500">{overallPct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className={['h-2 rounded-full transition-all duration-300',
                  paused ? 'bg-yellow-400' : 'bg-brand-600',
                ].join(' ')}
                style={{ width: `${overallPct}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-400">
                {formatBytes(uploadedBytes)} transferred
                {paused && <span className="ml-2 text-yellow-600 font-medium">· Upload paused</span>}
              </p>
              {uploading && !paused && uploadSpeed > 0 && (
                <p className="text-xs text-gray-500 flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${uploadSpeed > 5 * 1024 * 1024 ? 'bg-green-500' : uploadSpeed > 1 * 1024 * 1024 ? 'bg-yellow-500' : 'bg-red-500'}`} />
                  {formatBytes(uploadSpeed)}/s
                  {etaSeconds > 0 && <span className="text-gray-400">· ~{etaSeconds > 60 ? `${Math.round(etaSeconds / 60)}m` : `${etaSeconds}s`} left</span>}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        {files.length > 0 && (
          <div className="flex gap-2">
            {uploading && !paused ? (
              <>
                <button onClick={handlePause} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors duration-200">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Pause
                </button>
                <button onClick={() => {
                  // Stop the XHR immediately BEFORE the confirm dialog
                  // (window.confirm blocks JS but the upload continues in the browser)
                  abortControllerRef.current?.abort()
                  if (window.confirm('Abort upload? Files already uploaded will remain on Drive.')) {
                    handleAbort()
                  } else {
                    // User cancelled — file was reset to QUEUED by the catch block, resume upload
                    startUpload()
                  }
                }} className="btn-danger flex-1 flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Abort
                </button>
              </>
            ) : paused ? (
              <>
                <button onClick={startUpload} className="btn-primary flex-1 flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  Resume
                </button>
                <button onClick={handleCancelAll} className="btn-secondary flex-1 flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel all
                </button>
              </>
            ) : (
              <button onClick={startUpload} disabled={!canUpload} className="btn-primary w-full">
                {failedCount > 0 && queuedCount === 0
                  ? `Retry ${failedCount} failed ${failedCount === 1 ? 'file' : 'files'}`
                  : pickMode === 'compressed'
                    ? `Upload ${files.length} compressed ${files.length === 1 ? 'archive' : 'archives'}`
                    : `Upload ${files.length} ${files.length === 1 ? 'file' : 'files'}`
                }
              </button>
            )}
          </div>
        )}

      </main>
    </div>
  )
}

// ── Study-level validation banner ─────────────────────────────────
function StudyValidationBanner({ result, fileCount }) {
  const { status, checks } = result
  const colorMap = {
    PASSED:  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-800',  sub: 'text-green-600' },
    WARNING: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', sub: 'text-yellow-600' },
    FAILED:  { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-800',    sub: 'text-red-600' },
  }
  const c = colorMap[status] ?? colorMap.FAILED
  const label = {
    PASSED:  'Medical imaging dataset quality confirmed',
    WARNING: 'Medical imaging dataset quality — warnings present',
    FAILED:  'Medical imaging dataset quality issues detected',
  }[status]
  const keyChecks = ['Modality', 'Slice Thickness', 'Pixel Spacing', 'Image Rows', 'Image Columns']
  const displayed = checks.filter((ch) => keyChecks.includes(ch.name))
  const chipColor = { PASSED: 'bg-green-100 text-green-700', WARNING: 'bg-yellow-100 text-yellow-700', FAILED: 'bg-red-100 text-red-600' }

  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-4`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {status === 'PASSED' && <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          {status === 'WARNING' && <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>}
          {status === 'FAILED' && <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${c.text}`}>{label}</p>
          <p className={`text-xs ${c.sub} mt-0.5`}>
            Series assessment applied to all {fileCount} {fileCount === 1 ? 'file' : 'files'} — files always upload for admin review
          </p>
          {displayed.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {displayed.map((chk) => (
                <span key={chk.name} className={`text-xs font-medium px-2 py-0.5 rounded-full ${chipColor[chk.status] ?? chipColor.FAILED}`} title={chk.message}>
                  {chk.name}: {chk.value}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── File row ──────────────────────────────────────────────────────
function FileRow({ item, onRemove, uploading }) {
  const { file, status, progress, error, zipPeek, isCompressed } = item
  return (
    <li className="px-4 py-3 flex items-center gap-3">
      <div className="flex-shrink-0"><FileIcon status={status} isCompressed={isCompressed} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-sm text-gray-800 truncate">{file.name}</p>
          {isCompressed && <PendingBadge />}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{formatBytes(file.size)}</span>
          {isCompressed && zipPeek && (
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${zipPeek.dicom === zipPeek.total && zipPeek.total > 0 ? 'bg-green-50 text-green-700' : zipPeek.dicom > 0 ? 'bg-yellow-50 text-yellow-700' : 'bg-gray-100 text-gray-500'}`}>
              {zipPeek.dicom}/{zipPeek.total} DICOM inside
            </span>
          )}
        </div>
        {status === STATUS.VALIDATING && (
          <div className="mt-1 flex items-center gap-1.5"><Spinner className="w-3 h-3 text-yellow-500" /><span className="text-xs text-yellow-600">Validating DICOM…</span></div>
        )}
        {status === STATUS.PREPARING && (
          <div className="mt-1 flex items-center gap-1.5"><Spinner className="w-3 h-3 text-blue-500" /><span className="text-xs text-blue-600">Preparing…</span></div>
        )}
        {status === STATUS.UPLOADING && (
          <div className="mt-1.5 w-full bg-gray-100 rounded-full h-1.5">
            <div className="bg-brand-500 h-1.5 rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
          </div>
        )}
        {status === STATUS.FAILED && error && <p className="text-xs text-red-500 mt-0.5 truncate">{error}</p>}
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        <StatusBadge status={status} progress={progress} />
        {!uploading && ![STATUS.UPLOADING, STATUS.VALIDATING, STATUS.PREPARING].includes(status) && (
          <button onClick={onRemove} className="text-gray-300 hover:text-red-400 transition-colors p-0.5" title="Remove">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </li>
  )
}

function PendingBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 flex-shrink-0">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Pending review
    </span>
  )
}

function ValidationBadge({ status }) {
  if (status === 'PASSED') return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>Valid
    </span>
  )
  if (status === 'WARNING') return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 flex-shrink-0">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>Warn
    </span>
  )
  if (status === 'FAILED') return (
    <span className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 flex-shrink-0">
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>Invalid
    </span>
  )
  return null
}

function FileIcon({ status, isCompressed }) {
  if (status === STATUS.UPLOADED) return (
    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
    </div>
  )
  if (status === STATUS.FAILED) return (
    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
    </div>
  )
  if ([STATUS.UPLOADING, STATUS.VALIDATING, STATUS.PREPARING].includes(status)) return (
    <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center">
      <Spinner className="w-4 h-4 text-brand-600" />
    </div>
  )
  if (isCompressed) return (
    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
      <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    </div>
  )
  return (
    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
  )
}

function StatusBadge({ status, progress }) {
  const map = {
    [STATUS.QUEUED]:      { label: 'Queued',      cls: 'bg-gray-100 text-gray-500' },
    [STATUS.VALIDATING]:   { label: 'Validating',      cls: 'bg-yellow-100 text-yellow-600' },
    [STATUS.PREPARING]:   { label: 'Preparing',  cls: 'bg-blue-100 text-blue-600' },
    [STATUS.UPLOADING]:   { label: `${progress}%`, cls: 'bg-brand-100 text-brand-700' },
    [STATUS.UPLOADED]:    { label: 'Uploaded',    cls: 'bg-green-100 text-green-700' },
    [STATUS.FAILED]:      { label: 'Failed',      cls: 'bg-red-100 text-red-600' },
  }
  const { label, cls } = map[status] ?? map[STATUS.QUEUED]
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
}

function Spinner({ className = 'w-5 h-5' }) {
  return (
    <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}
